import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LanguageContext';
import { Tag, Plus, RefreshCw, ChevronLeft, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Purpose Tags Management Page
 * Manage purpose tags for functional groups with color coding
 */

// Color palette mapping
const COLOR_PALETTE = {
  blue: { bg: '#EBF5FF', text: '#0055FF', border: '#0055FF' },
  green: { bg: '#D1FAE5', text: '#10b981', border: '#10b981' },
  purple: { bg: '#EDE9FE', text: '#8b5cf6', border: '#8b5cf6' },
  orange: { bg: '#FFEDD5', text: '#f59e0b', border: '#f59e0b' },
  pink: { bg: '#FCE7F3', text: '#ec4899', border: '#ec4899' },
  yellow: { bg: '#FEF3C7', text: '#fbbf24', border: '#fbbf24' },
  red: { bg: '#FEE2E2', text: '#ef4444', border: '#ef4444' },
  teal: { bg: '#CCFBF1', text: '#14b8a6', border: '#14b8a6' }
};

const COLOR_OPTIONS = Object.keys(COLOR_PALETTE);

const PurposeTagsPage = () => {
  const { user, isAdmin } = useAuth();
  const t = useLang();
  const navigate = useNavigate();
  
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    color: 'blue',
    description: ''
  });

  // Check admin access
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
    } else {
      fetchTags();
    }
  }, [isAdmin, navigate]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/purpose-tags`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load purpose tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/purpose-tags`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        toast.success('Purpose tag created successfully');
        setShowCreateModal(false);
        setFormData({ name: '', color: 'blue', description: '' });
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create tag');
      }
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    }
  };

  const handleEditTag = async () => {
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/purpose-tags/${selectedTag.tag_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        toast.success('Purpose tag updated successfully');
        setShowEditModal(false);
        setSelectedTag(null);
        setFormData({ name: '', color: 'blue', description: '' });
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update tag');
      }
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error('Failed to update tag');
    }
  };

  const handleDeleteTag = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/purpose-tags/${selectedTag.tag_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success('Purpose tag deleted successfully');
        setShowDeleteModal(false);
        setSelectedTag(null);
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete tag');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
    }
  };

  const openEditModal = (tag) => {
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      description: tag.description || ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (tag) => {
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  if (!isAdmin()) {
    return null;
  }

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: EMZI_COLORS.background,
      overflow: 'hidden'
    }}>
      {/* Fixed Header - EMZI CI Style */}
      <div style={{
        background: 'linear-gradient(135deg, #0055FF 0%, #0044CC 100%)',
        padding: '20px',
        color: 'white',
        boxShadow: EMZI_SHADOWS.lg,
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Tag size={28} strokeWidth={2} />
              {t('admin.purposeTags.title')}
            </h1>
            <p style={{
              fontSize: '14px',
              opacity: 0.9,
              margin: '4px 0 0 0'
            }}>
              {t('admin.purposeTags.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Stats Bar */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px',
        background: EMZI_COLORS.background,
        flexShrink: 0,
        overflowX: 'auto'
      }}>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            {t('admin.purposeTags.totalTags')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: EMZI_COLORS.primary
          }}>
            {loading ? '...' : tags.length}
          </div>
        </div>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            {t('admin.purposeTags.inUse')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#10b981'
          }}>
            {loading ? '...' : tags.filter(t => t.usage_count > 0).length}
          </div>
        </div>
      </div>

      {/* Fixed Action Buttons */}
      <div style={{
        padding: '0 20px 16px',
        background: EMZI_COLORS.background,
        flexShrink: 0,
        display: 'flex',
        gap: '12px'
      }}>
        <button
          onClick={() => {
            setFormData({ name: '', color: 'blue', description: '' });
            setShowCreateModal(true);
          }}
          style={{
            flex: '1',
            padding: '12px 20px',
            background: EMZI_COLORS.primary,
            color: 'white',
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
            boxShadow: EMZI_SHADOWS.sm,
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = EMZI_SHADOWS.md;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
          }}
        >
          <Plus size={18} strokeWidth={2} />
          {t('admin.purposeTags.createTag')}
        </button>
        <button
          onClick={fetchTags}
          disabled={loading}
          style={{
            padding: '12px 20px',
            background: 'white',
            color: EMZI_COLORS.textPrimary,
            border: `2px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
            transition: 'transform 0.2s',
            opacity: loading ? 0.6 : 1
          }}
          onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.02)')}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <RefreshCw size={18} strokeWidth={2} />
        </button>
      </div>

      {/* Scrollable Tags List Container */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '0 20px 20px',
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth'
      }}>
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: EMZI_COLORS.textSecondary
          }}>
            Loading tags...
          </div>
        ) : tags.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            boxShadow: EMZI_SHADOWS.sm
          }}>
            <Tag size={48} color={EMZI_COLORS.textSecondary} style={{ marginBottom: '16px' }} />
            <p style={{ color: EMZI_COLORS.textSecondary, margin: 0 }}>
              {t('admin.purposeTags.noTags')}
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px',
            paddingBottom: '80px'
          }}>
            {tags.map((tag) => {
              const colorStyle = COLOR_PALETTE[tag.color] || COLOR_PALETTE.blue;
              return (
                <div
                  key={tag.tag_id}
                  style={{
                    background: 'white',
                    borderRadius: EMZI_RADIUS.lg,
                    padding: '16px',
                    boxShadow: EMZI_SHADOWS.sm,
                    border: `1px solid ${EMZI_COLORS.border}`,
                    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                    marginBottom: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = EMZI_SHADOWS.md;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px'
                      }}>
                        <span style={{
                          padding: '4px 12px',
                          background: colorStyle.bg,
                          color: colorStyle.text,
                          border: `1px solid ${colorStyle.border}`,
                          borderRadius: EMZI_RADIUS.sm,
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          {tag.name}
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: EMZI_COLORS.textSecondary
                        }}>
                          {tag.usage_count} {t('admin.purposeTags.groups')}
                        </span>
                      </div>
                      {tag.description && (
                        <div style={{
                          fontSize: '12px',
                          color: EMZI_COLORS.textSecondary,
                          marginTop: '4px'
                        }}>
                          {tag.description}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button
                        onClick={() => openEditModal(tag)}
                        style={{
                          padding: '6px',
                          background: EMZI_COLORS.surface,
                          border: 'none',
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e0e7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = EMZI_COLORS.surface}
                      >
                        <Edit2 size={16} color={EMZI_COLORS.primary} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(tag)}
                        style={{
                          padding: '6px',
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}
                        title="Delete Tag"
                      >
                        <Trash2 size={16} color="#dc2626" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: EMZI_SHADOWS.xl
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${EMZI_COLORS.border}`
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: EMZI_COLORS.textPrimary,
                margin: 0
              }}>
                {t('admin.purposeTags.createTag')}
              </h2>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.tagName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sales, Support, Operations"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.color')} *
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px'
                }}>
                  {COLOR_OPTIONS.map((color) => {
                    const colorStyle = COLOR_PALETTE[color];
                    return (
                      <button
                        key={color}
                        onClick={() => setFormData({ ...formData, color })}
                        style={{
                          padding: '12px',
                          background: colorStyle.bg,
                          border: formData.color === color 
                            ? `3px solid ${colorStyle.border}`
                            : `1px solid ${EMZI_COLORS.border}`,
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: colorStyle.text,
                          textTransform: 'capitalize',
                          transition: 'all 0.2s'
                        }}
                      >
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${EMZI_COLORS.border}`,
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ name: '', color: 'blue', description: '' });
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: EMZI_COLORS.textPrimary,
                  border: `2px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateTag}
                disabled={!formData.name.trim()}
                style={{
                  padding: '10px 20px',
                  background: formData.name.trim() ? EMZI_COLORS.primary : '#e5e7eb',
                  color: formData.name.trim() ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: formData.name.trim() ? 'pointer' : 'not-allowed',
                  minHeight: '44px'
                }}
              >
                {t('admin.purposeTags.createTag')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTag && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: EMZI_SHADOWS.xl
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${EMZI_COLORS.border}`
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: EMZI_COLORS.textPrimary,
                margin: 0
              }}>
                {t('admin.purposeTags.editTag')}
              </h2>
            </div>

            {/* Body - Same as Create Modal */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.tagName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sales, Support, Operations"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.color')} *
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px'
                }}>
                  {COLOR_OPTIONS.map((color) => {
                    const colorStyle = COLOR_PALETTE[color];
                    return (
                      <button
                        key={color}
                        onClick={() => setFormData({ ...formData, color })}
                        style={{
                          padding: '12px',
                          background: colorStyle.bg,
                          border: formData.color === color 
                            ? `3px solid ${colorStyle.border}`
                            : `1px solid ${EMZI_COLORS.border}`,
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: colorStyle.text,
                          textTransform: 'capitalize',
                          transition: 'all 0.2s'
                        }}
                      >
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.purposeTags.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${EMZI_COLORS.border}`,
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTag(null);
                  setFormData({ name: '', color: 'blue', description: '' });
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: EMZI_COLORS.textPrimary,
                  border: `2px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleEditTag}
                disabled={!formData.name.trim()}
                style={{
                  padding: '10px 20px',
                  background: formData.name.trim() ? EMZI_COLORS.primary : '#e5e7eb',
                  color: formData.name.trim() ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: formData.name.trim() ? 'pointer' : 'not-allowed',
                  minHeight: '44px'
                }}
              >
                {t('admin.purposeTags.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTag && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            maxWidth: '400px',
            width: '100%',
            boxShadow: EMZI_SHADOWS.xl
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${EMZI_COLORS.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle size={24} color="#dc2626" />
              </div>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: EMZI_COLORS.textPrimary,
                  margin: 0
                }}>
                  {t('admin.purposeTags.deleteTag')}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              {selectedTag.usage_count > 0 ? (
                <div style={{
                  padding: '12px',
                  background: '#fef3c7',
                  borderRadius: EMZI_RADIUS.md,
                  border: '1px solid #fbbf24'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#92400e',
                    marginBottom: '4px'
                  }}>
                    ⚠️ {t('admin.purposeTags.cannotDelete')}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#78350f'
                  }}>
                    {t('admin.purposeTags.tagInUse').replace('{count}', selectedTag.usage_count)}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: EMZI_COLORS.textPrimary,
                    margin: '0 0 12px 0'
                  }}>
                    {t('admin.purposeTags.confirmDelete')}
                  </p>
                  <div style={{
                    padding: '12px',
                    background: EMZI_COLORS.surface,
                    borderRadius: EMZI_RADIUS.md
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      background: COLOR_PALETTE[selectedTag.color].bg,
                      color: COLOR_PALETTE[selectedTag.color].text,
                      border: `1px solid ${COLOR_PALETTE[selectedTag.color].border}`,
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {selectedTag.name}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{
              padding: '0 24px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTag(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: EMZI_COLORS.textPrimary,
                  border: `2px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                {selectedTag.usage_count > 0 ? t('common.close') : t('common.cancel')}
              </button>
              {selectedTag.usage_count === 0 && (
                <button
                  onClick={handleDeleteTag}
                  style={{
                    padding: '10px 20px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Trash2 size={18} />
                  {t('admin.purposeTags.confirmDeleteBtn')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurposeTagsPage;
